"use client"

import { deleteToken, getToken, onMessage } from "firebase/messaging"
import {
  getFirebaseMessaging,
  getFirebaseVapidKey,
  isFirebaseMessagingConfigured,
} from "@/lib/firebase/client"

const SW_PATH = "/firebase-messaging-sw.js"

export function isBrowserPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator
  )
}

export function getNotificationPermission(): NotificationPermission | "unsupported" {
  if (!isBrowserPushSupported()) return "unsupported"
  return Notification.permission
}

export async function registerMessagingServiceWorker(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration(SW_PATH)
  if (existing) {
    return existing
  }
  return navigator.serviceWorker.register(SW_PATH, { scope: "/" })
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!isBrowserPushSupported()) {
    return "denied"
  }
  return Notification.requestPermission()
}

export async function obtainFcmToken(): Promise<string | null> {
  if (!isFirebaseMessagingConfigured() || !isBrowserPushSupported()) {
    return null
  }

  const permission = await requestNotificationPermission()
  if (permission !== "granted") {
    return null
  }

  const registration = await registerMessagingServiceWorker()
  await navigator.serviceWorker.ready

  const messaging = getFirebaseMessaging()
  const token = await getToken(messaging, {
    vapidKey: getFirebaseVapidKey(),
    serviceWorkerRegistration: registration,
  })
  return token || null
}

export async function revokeFcmToken(): Promise<void> {
  if (!isFirebaseMessagingConfigured()) return
  try {
    const messaging = getFirebaseMessaging()
    await deleteToken(messaging)
  } catch {
    // Token may already be absent
  }
}

export function subscribeToForegroundMessages(
  handler: (payload: {
    title?: string
    body?: string
    data?: Record<string, string>
  }) => void,
): () => void {
  if (!isFirebaseMessagingConfigured()) {
    return () => undefined
  }
  const messaging = getFirebaseMessaging()
  return onMessage(messaging, (payload) => {
    handler({
      title: payload.notification?.title,
      body: payload.notification?.body,
      data: payload.data as Record<string, string> | undefined,
    })
  })
}
